import { useRef, useCallback } from 'react'
import { dia } from '@joint/core'
import { useCSVStore } from '../../../map-editor-features/csv/model/csvStore'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { useProjectStore } from '../../../map-editor-shared/store/projectStore'
import { type ParseResult } from '../../../map-editor-features/csv/lib/csvParser'
import { type GroupedLayer } from '../../../map-editor-features/csv/lib/layerGrouper'

export interface UseCSVUploadParams {
  graph: dia.Graph | null
  processCSVData: (text: string, fileName: string) => Promise<void>
  setErrorModal: (modal: { show: boolean; message: string }) => void
  setElementCount: (count: number) => void
  setObjectsByLayer: (map: Map<string, dia.Element[]>) => void
}

export interface FloorData {
  id: string
  mapData?: {
    csvRawData?: string
    csvFileName?: string
    csvParsedData?: ParseResult | null
    csvGroupedLayers?: GroupedLayer[] | null
    csvSelectedLayers?: string[]
  }
}

export interface UseCSVUploadReturn {
  csvInputRef: React.RefObject<HTMLInputElement>
  triggerFileUpload: () => void
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  handleClearCanvas: () => void
  restoreCSVState: (floorData: FloorData | undefined) => void
}

export const useCSVUpload = ({
  graph,
  processCSVData,
  setErrorModal,
  setElementCount,
  setObjectsByLayer,
}: UseCSVUploadParams): UseCSVUploadReturn => {
  const csvInputRef = useRef<HTMLInputElement>(null)
  const setFile = useCSVStore(s => s.setFile)
  const setUploadState = useCSVStore(s => s.setUploadState)
  const { currentFloor, setCurrentFloor, addFloor, getFloorsByLotId } = useFloorStore()
  const { currentLot } = useProjectStore()

  // Trigger file upload dialog
  const triggerFileUpload = useCallback(() => {
    csvInputRef.current?.click()
  }, [])

  // Handle file upload
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      if (!file.name.endsWith('.csv')) {
        setErrorModal({ show: true, message: 'CSV 파일만 업로드 가능합니다.' })
        return
      }

      // CRITICAL FIX: Ensure floor exists before CSV upload
      // If no floor exists, create default "B1" floor
      if (!currentFloor && currentLot) {
        const existingFloors = getFloorsByLotId(currentLot)
        if (existingFloors.length === 0) {
          // Create default floor with name "B1"
          addFloor(currentLot)
          // Get the newly created floor ID
          const updatedFloors = useFloorStore.getState().getFloorsByLotId(currentLot)
          const newFloor = updatedFloors[updatedFloors.length - 1]
          if (newFloor) {
            // Update floor name to B1
            useFloorStore.getState().updateFloor(newFloor.id, { name: 'B1' })
            // Set as current floor
            setCurrentFloor(newFloor.id)
          }
        } else {
          // Use first available floor
          setCurrentFloor(existingFloors[0].id)
        }
      }

      setFile(file)
      setUploadState({ status: 'uploading', progress: 0 })

      const reader = new FileReader()
      reader.onload = async (e) => {
        const text = e.target?.result as string
        await processCSVData(text, file.name)
        setUploadState({ status: 'idle' })
      }
      reader.readAsText(file)
    },
    [processCSVData, setErrorModal, setFile, setUploadState, currentFloor, currentLot, addFloor, setCurrentFloor, getFloorsByLotId]
  )

  // Clear canvas - remove CSV entities, keep and reset template objects
  const handleClearCanvas = useCallback(() => {
    if (!graph) return

    if (
      window.confirm(
        '레이어 매핑된 객체를 삭제하고 템플릿 객체의 속성과 관계를 초기화하시겠습니까?'
      )
    ) {
      // Remove all CSV-imported (layer-mapped) objects
      const elementsToRemove: dia.Element[] = []
      const templateElements: dia.Element[] = []

      graph.getElements().forEach((element) => {
        const data = element.get('data') || {}
        if (data.type === 'csv-entity') {
          // Mark CSV entities for removal
          elementsToRemove.push(element)
        } else {
          // Keep template objects but reset them
          templateElements.push(element)
        }
      })

      // Remove CSV entities
      elementsToRemove.forEach((element) => element.remove())

      // Reset template objects (clear properties and relationships)
      templateElements.forEach((element) => {
        const data = element.get('data') || {}
        const resetData = {
          ...data,
          properties: {
            name: data.properties?.name || '', // Keep name but reset other properties
          },
        }
        element.set('data', resetData)
      })

      // Remove all links (relationships)
      graph.getLinks().forEach((link) => link.remove())

      // Update counts
      setElementCount(graph.getElements().length)

      // Rebuild objectsByLayer map (only template objects remain)
      const newObjectsByLayer = new Map<string, dia.Element[]>()
      graph.getElements().forEach((element) => {
        const data = element.get('data') || {}
        const layer = data.layer || 'default'

        if (!newObjectsByLayer.has(layer)) {
          newObjectsByLayer.set(layer, [])
        }
        newObjectsByLayer.get(layer)!.push(element)
      })
      setObjectsByLayer(newObjectsByLayer)

    }
  }, [graph, setElementCount, setObjectsByLayer])

  // Restore CSV state when switching floors
  const restoreCSVState = useCallback((floorData: FloorData | undefined) => {
    if (floorData?.mapData?.csvRawData) {
      // Floor has saved CSV data - restore it
      useCSVStore.setState({
        uploadState: {
          status: 'parsed',
          fileName: floorData.mapData.csvFileName || 'Restored Data',
          rowCount: floorData.mapData.csvParsedData?.rowCount || 0,
          isRestored: true,
        },
        rawData: floorData.mapData.csvRawData,
        parsedData: floorData.mapData.csvParsedData || null,
        groupedLayers: floorData.mapData.csvGroupedLayers || null,
        selectedLayers: new Set(floorData.mapData.csvSelectedLayers || []),
        file: null,
      })
    } else {
      // No saved data - clear CSV state to show upload UI
      useCSVStore.setState({
        uploadState: { status: 'idle' },
        file: null,
        rawData: null,
        parsedData: null,
        groupedLayers: null,
        selectedLayers: new Set<string>(),
      })
    }
  }, [])

  return {
    csvInputRef,
    triggerFileUpload,
    handleFileChange,
    handleClearCanvas,
    restoreCSVState,
  }
}
